import { z } from "zod";
import { dueDiligenceTierSchema } from "./DueDiligence";

export enum ClientUserSsoProviderType {
  GOOGLE = "GOOGLE",
  LINKEDIN = "LINKEDIN",
}

export const clientUserDTO = z.object({
  id: z.string(),
  email: z.string(),
  phone: z.string(),
  name: z.string(),
});

export type ClientUserDTO = z.infer<typeof clientUserDTO>;

export enum BusinessSubscriptionPlanType {
  CASUAL = "CASUAL",
  PLUS = "PLUS",
  ENTERPRISE = "ENTERPRISE",
}

export enum MemberStatus {
  ACTIVE = "ACTIVE",
  INVITED = "INVITED",
}

export enum MemberRol {
  OWNER = "OWNER",
  ADMINISTRATIVE = "ADMINISTRATIVE",
  AGENT = "AGENT",
  DEVELOPER = "DEVELOPER"
}

export const invitedMemberDataPreActivationDTO = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string(),
});

export const memberDTO = z.object({
  memberId: z.string(),
  client: clientUserDTO.optional(),
  status: z.nativeEnum(MemberStatus),
  rol: z.nativeEnum(MemberRol),
  invitedMemberDataPreActivation: invitedMemberDataPreActivationDTO.optional(),
});

export type MemberDTO = z.infer<typeof memberDTO>;

export const businessDueDiligencePlanDTO = z.object({
  lookupKey: z.string(),
  paidUntil: z.string().transform((date) => new Date(date)),
  monthlyCredits: z.number().nullable(),
  includesChat: z.boolean().nullable(),
  allowedTiers: z.array(dueDiligenceTierSchema).nullable().optional(),
});

export type BusinessDueDiligencePlanDTO = z.infer<typeof businessDueDiligencePlanDTO>;

export const businessCreditsDTO = z.object({
  balance: z.number(),
  dueDiligencePlan: businessDueDiligencePlanDTO.nullable(),
});

export type BusinessCreditsDTO = z.infer<typeof businessCreditsDTO>;

export const copilotSubscriptionDTO = z.object({
  /** As Stripe tells it: active, trialing, past_due, incomplete, canceled... */
  status: z.string().nullable(),
  cancelAt: z.string().nullable().transform((date) => (date ? new Date(date) : null)),
});

export type CopilotSubscriptionDTO = z.infer<typeof copilotSubscriptionDTO>;

export const businessDTO = z.object({
  id: z.string(),
  name: z.string(),
  creationDate: z.string().transform((date) => new Date(date)),
  memberAllowedQuantity: z.number(),
  availableDueDiligenceReports: z.number().optional().nullable(),
  members: z.array(memberDTO),
  trialEndDate: z.string().nullable().optional().transform((date) => date ? new Date(date) : null),
  isStaffOwned: z.boolean().optional().default(false),
  credits: businessCreditsDTO.optional(),
  /** Only GET /business sends it, null when there is none; other responses leave the one already read. */
  copilotSubscription: copilotSubscriptionDTO.nullable().optional(),
});

export type BusinessDTO = z.infer<typeof businessDTO>;
